import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { formatMoney } from "@bw/shared/utils";
import {
  isApiError,
  isBookingCancellationQuoteExpired,
  isValidBookingCancellationQuote,
  requiresExactCancellationRetry,
} from "@bw/shared/api";
import type {
  BookingCancellationQuoteResponse,
  BookingResponse,
} from "@bw/shared/types/generated";

function expiryLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unavailable";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}

/** Cross-facility confirmation modal driven by one server-issued quote. */
export function CancelBookingDialog({
  booking,
  quote,
  quoteLoading,
  quoteError,
  onQuoteRetry,
  onConfirm,
  onClose,
  cancelPending,
  cancelError,
  decisionChanged,
}: {
  booking: BookingResponse;
  quote: BookingCancellationQuoteResponse | undefined;
  quoteLoading: boolean;
  quoteError: unknown;
  onQuoteRetry: () => void;
  onConfirm: () => void;
  onClose: () => void;
  cancelPending: boolean;
  cancelError: unknown;
  decisionChanged: boolean;
}) {
  const keepButtonRef = useRef<HTMLButtonElement>(null);
  const quoteResultRef = useRef<HTMLDListElement>(null);
  const [confirmInitiated, setConfirmInitiated] = useState(false);
  const [retryRequested, setRetryRequested] = useState(false);
  const [failureAt, setFailureAt] = useState<string | null>(null);
  const [, setExpiryTick] = useState(0);

  const quoteValid = isValidBookingCancellationQuote(quote, booking.booking_id);
  const displayCurrency = quoteValid ? quote.currency.toUpperCase() : "";
  const expiryMs = quoteValid ? Date.parse(quote.expires_at) : Number.NaN;
  const quoteExpired = quoteValid && isBookingCancellationQuoteExpired(quote);
  const exactRetryRequired = requiresExactCancellationRetry(cancelError);
  const mutationLocked = cancelPending || confirmInitiated;
  const closeLocked = mutationLocked || exactRetryRequired;
  const confirmDisabled =
    mutationLocked ||
    (!exactRetryRequired &&
      (quoteLoading || Boolean(quoteError) || !quoteValid || quoteExpired));

  useEffect(() => {
    if (!quoteValid || quoteExpired) return;
    const delay = Math.min(expiryMs - Date.now() + 25, 2_147_483_647);
    const timer = window.setTimeout(
      () => setExpiryTick((value) => value + 1),
      delay,
    );
    return () => window.clearTimeout(timer);
  }, [expiryMs, quoteExpired, quoteValid]);

  useEffect(() => {
    if (cancelError) setConfirmInitiated(false);
  }, [cancelError]);

  useEffect(() => {
    if (!quoteLoading && (quoteError || (!quote && !quoteValid))) {
      setFailureAt(new Date().toISOString());
    }
  }, [quote, quoteError, quoteLoading, quoteValid]);

  useEffect(() => {
    if (!retryRequested || quoteLoading || quoteError || !quoteValid) return;
    quoteResultRef.current?.focus();
    setRetryRequested(false);
  }, [quoteError, quoteLoading, quoteValid, retryRequested]);

  useEffect(() => {
    keepButtonRef.current?.focus();
  }, []);

  function retryQuote() {
    if (quoteLoading || exactRetryRequired) return;
    setRetryRequested(true);
    onQuoteRetry();
  }

  const supportHref = `mailto:support@badmintonwarriors.com?subject=${encodeURIComponent(
    `Cancellation quote failed for booking ${booking.booking_id}`,
  )}&body=${encodeURIComponent(
    [
      `Booking: ${booking.booking_id}`,
      `Facility: ${booking.facility_id}`,
      `Failure time: ${failureAt ?? new Date().toISOString()}`,
    ].join("\n"),
  )}`;

  return (
    <Dialog
      open
      aria-label="Cancel this booking"
      aria-describedby="cancel-refund-quote"
      onClose={(_event, reason) => {
        if (reason === "backdropClick" && closeLocked) return;
        if (!closeLocked) onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Cancel this booking?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {decisionChanged ? (
            <Alert severity="warning">
              The cancellation decision changed. Review the updated fee and
              refund, then confirm again.
            </Alert>
          ) : null}

          <Stack
            component="section"
            id="cancel-refund-quote"
            aria-label="Cancellation quote"
            spacing={1.5}
          >
            {quoteLoading ? (
              <Stack
                direction="row"
                spacing={1.5}
                role="status"
                sx={{ alignItems: "center" }}
              >
                <CircularProgress size={20} />
                <Typography color="text.secondary">
                  Loading cancellation quote…
                </Typography>
              </Stack>
            ) : quoteError || !quoteValid ? (
              <Stack spacing={1.25} sx={{ alignItems: "flex-start" }}>
                <Alert severity="error">
                  We couldn&apos;t load a valid cancellation quote. Retry before
                  cancelling this booking.
                </Alert>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={quoteLoading}
                  onClick={retryQuote}
                >
                  {quoteLoading ? "Refreshing quote…" : "Refresh quote"}
                </Button>
                <Link href={supportHref}>Contact support</Link>
              </Stack>
            ) : (
              <>
                <Box
                  component="dl"
                  ref={quoteResultRef}
                  role="status"
                  aria-label="Cancellation quote"
                  aria-live="polite"
                  tabIndex={-1}
                  sx={{ m: 0 }}
                >
                  <QuoteRow
                    label="Cancellation fee"
                    value={`${formatMoney(quote.fee_amount, displayCurrency)} ${displayCurrency} · ${String(quote.fee_percentage)}%`}
                  />
                  <QuoteRow
                    label="Refund"
                    value={`${formatMoney(quote.refund_amount, displayCurrency)} ${displayCurrency}`}
                    emphasized
                  />
                </Box>
                {quoteExpired ? (
                  <Stack spacing={1.25} sx={{ alignItems: "flex-start" }}>
                    <Alert severity="error">
                      {exactRetryRequired
                        ? "The reviewed quote expired, but the prior cancellation may still be processing. Retry the exact same cancellation."
                        : "This cancellation quote expired. Refresh it before cancelling."}
                    </Alert>
                    {!exactRetryRequired ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={retryQuote}
                      >
                        Refresh quote
                      </Button>
                    ) : null}
                  </Stack>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    Quote expires at{" "}
                    <time dateTime={quote.expires_at}>
                      {expiryLabel(quote.expires_at)}
                    </time>
                    .
                  </Typography>
                )}
              </>
            )}
          </Stack>

          {cancelError && !decisionChanged ? (
            <Alert severity="error">
              {isApiError(cancelError)
                ? cancelError.message || "We couldn't cancel this booking."
                : "We couldn't cancel this booking. Please try again."}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          ref={keepButtonRef}
          variant="text"
          size="small"
          onClick={onClose}
          disabled={closeLocked}
        >
          Keep booking
        </Button>
        <Button
          color="error"
          variant="contained"
          size="small"
          onClick={() => {
            if (confirmDisabled) return;
            setConfirmInitiated(true);
            onConfirm();
          }}
          disabled={confirmDisabled}
        >
          {mutationLocked
            ? "Cancelling…"
            : exactRetryRequired
              ? "Retry exact cancellation"
              : decisionChanged
                ? "Confirm updated cancellation"
                : "Yes, cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function QuoteRow({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
        justifyContent: "space-between",
      }}
    >
      <Typography component="dt" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="dd"
        sx={{ m: 0, fontWeight: emphasized ? 700 : 500, textAlign: "right" }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
